import api, { API_URL } from "./api";
import authService from "./authService";

const buildSseUrl = (path, params) => {
  const base = API_URL.endsWith("/") ? API_URL : `${API_URL}/`;
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  const url = new URL(normalizedPath, base.startsWith("http") ? base : window.location.origin + base);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

const recareService = {
  // Dashboard endpoints
  getOverview: (locationId) => api.get("/recare/dashboard/overview", locationId ? { headers: { "X-Location-Id": locationId } } : {}),
  getSegmentation: (locationId) => api.get("/recare/dashboard/segmentation", locationId ? { headers: { "X-Location-Id": locationId } } : {}),
  getFunnel: (locationId) => api.get("/recare/dashboard/funnel", locationId ? { headers: { "X-Location-Id": locationId } } : {}),
  getChannels: (days = 90, locationId) => api.get(`/recare/dashboard/channels?days=${days}`, locationId ? { headers: { "X-Location-Id": locationId } } : {}),
  getTrends: (locationId) => api.get("/recare/dashboard/trends", locationId ? { headers: { "X-Location-Id": locationId } } : {}),
  getAgents: (days = 90, locationId) => api.get(`/recare/dashboard/agents?days=${days}`, locationId ? { headers: { "X-Location-Id": locationId } } : {}),
  getRoi: (locationId) => api.get("/recare/dashboard/roi", locationId ? { headers: { "X-Location-Id": locationId } } : {}),
  getTargets: (limit = 20, locationId) => api.get(`/recare/dashboard/targets?limit=${limit}`, locationId ? { headers: { "X-Location-Id": locationId } } : {}),
  getBookedAppointments: (limit = 20, locationId) => api.get(`/recare/dashboard/booked-appointments?limit=${limit}`, locationId ? { headers: { "X-Location-Id": locationId } } : {}),
  getBucketPatients: (
    segment,
    page = 1,
    limit = 20,
    locationId,
    search = "",
    attempts = "",
  ) =>
    api.get(
      `/recare/dashboard/bucket-patients?segment=${encodeURIComponent(segment)}&page=${page}&limit=${limit}&search=${encodeURIComponent(search || "")}&attempts=${encodeURIComponent(attempts || "")}`,
      locationId ? { headers: { "X-Location-Id": locationId } } : {},
    ),
  getD0120ProfilePatients: (type, page = 1, limit = 20, locationId) =>
    api.get(
      `/recare/dashboard/d0120-profile-patients?type=${encodeURIComponent(type)}&page=${page}&limit=${limit}`,
      locationId ? { headers: { "X-Location-Id": locationId } } : {},
    ),
  exportRecarePool: (locationId) =>
    api.get(
      "/recare/dashboard/export-pool",
      {
        responseType: "blob",
        ...(locationId ? { headers: { "X-Location-Id": locationId } } : {}),
      },
    ),

  // Campaign management
  createCampaign: () => api.post("/recare/campaigns"),
  assignLeads: (campaignId, data) => api.post(`/recare/campaigns/${campaignId}/assign`, data),

  /**
   * SSE stream for building the recare pool with real-time progress.
   * Returns an EventSource. Caller should listen on 'message' and 'error'.
   */
  buildPoolStream: async (overrideLocationId) => {
    const locationId = overrideLocationId || localStorage.getItem("currentLocationId");
    const tokenData = await authService.issueUrlAccessToken({
      scope: "recare_sse",
      ...(locationId ? { locationId } : {}),
    });
    const token = tokenData?.data?.token ?? tokenData?.token;
    const url = buildSseUrl("recare/campaigns/build-stream", {
      token,
      locationId,
    });
    // withCredentials: send session cookie when API is cross-origin (still prefer ?token= on server)
    return new EventSource(url, { withCredentials: true });
  },

  // Campaign patient operations
  updatePatientStatus: (id, data) => api.patch(`/recare/campaign-patients/${id}/status`, data),
  logBooking: (id, data) => api.post(`/recare/campaign-patients/${id}/booking`, data),

  // Lead queue (priority-sorted, paginated)
  getLeadQueue: (params = {}) => api.get("/recare/lead-queue", { params }),

  // Provider list for appointment booking
  getProviders: () => api.get("/recare/providers"),

  // AI brief for agents
  getPatientBrief: (patientId) => api.get(`/recare/patient/${patientId}/brief`),

  // Existing recare endpoints
  getRecarePatients: (params) => api.get("/recare/patients", { params }),
  getRecareStats: () => api.get("/recare/stats"),
  scheduleAppointment: (data) => api.post("/recare/schedule", data),
  assignPipeline: (data) => api.post("/recare/assign-pipeline", data),
};

export default recareService;
